import {
  startAuthentication,
  startRegistration,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from "@simplewebauthn/browser";
import { createContext, useCallback, useContext, useMemo, useSyncExternalStore, type ReactNode } from "react";
import { domainError, useTRPCClient } from "./api";
import { presenceStore, type PresenceStore } from "./presence";

/** The browser half of WebAuthn, behind a seam so component tests can stand in for Touch ID. */
export interface WebAuthnBrowser {
  startRegistration(options: unknown): Promise<unknown>;
  startAuthentication(options: unknown): Promise<{ id: string } & Record<string, unknown>>;
}

export const browserWebAuthn: WebAuthnBrowser = {
  startRegistration: (options) => startRegistration({ optionsJSON: options as PublicKeyCredentialCreationOptionsJSON }),
  startAuthentication: async (options) => {
    const response = await startAuthentication({ optionsJSON: options as PublicKeyCredentialRequestOptionsJSON });
    return { ...response };
  },
};

interface PresenceDeps {
  store: PresenceStore;
  webauthn: WebAuthnBrowser;
}

const PresenceContext = createContext<PresenceDeps>({ store: presenceStore, webauthn: browserWebAuthn });

/** Tests (and only tests) swap the store and the WebAuthn calls. */
export function PresenceProvider({ store, webauthn, children }: PresenceDeps & { children: ReactNode }) {
  const value = useMemo(() => ({ store, webauthn }), [store, webauthn]);
  return <PresenceContext.Provider value={value}>{children}</PresenceContext.Provider>;
}

/**
 * `present`: a valid token is in memory. `no_passkey`: the person has to enrol one first
 * (Settings → Security). `cancelled`: they dismissed Touch ID or the security-key prompt.
 */
export type PresenceOutcome = "present" | "no_passkey" | "cancelled";

/** A dismissed or timed-out WebAuthn prompt; anything else is a real failure. */
export function isCancelled(error: unknown): boolean {
  return error instanceof Error && (error.name === "NotAllowedError" || error.name === "AbortError");
}

/** One assertion at a time per store: two Approve clicks share a single Touch ID prompt. */
const inFlight = new WeakMap<PresenceStore, Promise<PresenceOutcome>>();

export function usePresence() {
  const { store, webauthn } = useContext(PresenceContext);
  const client = useTRPCClient();
  const token = useSyncExternalStore(store.subscribe, store.token);

  /**
   * Makes sure a presence token is in memory: if there is none, runs the passkey assertion
   * (`presence.authenticationOptions` → Touch ID → `presence.authenticate`) and keeps the token
   * the api returns. The caller decides what to do on `no_passkey` and `cancelled`.
   */
  const ensurePresence = useCallback((): Promise<PresenceOutcome> => {
    if (store.token() !== null) return Promise.resolve("present");
    const running = inFlight.get(store);
    if (running !== undefined) return running;
    const run = (async (): Promise<PresenceOutcome> => {
      let options: unknown;
      try {
        options = await client.presence.authenticationOptions.mutate();
      } catch (error) {
        if (domainError(error)?.reason === "no_passkey") return "no_passkey";
        throw error;
      }
      let response: { id: string } & Record<string, unknown>;
      try {
        response = await webauthn.startAuthentication(options);
      } catch (error) {
        if (isCancelled(error)) return "cancelled";
        throw error;
      }
      const { token: minted, expiresAt } = await client.presence.authenticate.mutate({ response });
      store.set(minted, expiresAt);
      return "present";
    })().finally(() => inFlight.delete(store));
    inFlight.set(store, run);
    return run;
  }, [client, store, webauthn]);

  /** Enrols a new passkey under `label` (`presence.registrationOptions` → create → `register`). */
  const registerPasskey = useCallback(
    async (label: string): Promise<"registered" | "cancelled"> => {
      const options = await client.presence.registrationOptions.mutate();
      let response: unknown;
      try {
        response = await webauthn.startRegistration(options);
      } catch (error) {
        if (isCancelled(error)) return "cancelled";
        throw error;
      }
      await client.presence.register.mutate({ response, label });
      return "registered";
    },
    [client, webauthn],
  );

  return {
    present: token !== null,
    expiresAt: token === null ? null : store.expiresAt(),
    ensurePresence,
    registerPasskey,
    /** Forgets the token now (e.g. "Lock" in Settings, or after the api refused it). */
    lock: () => store.clear(),
  };
}
